import { IAuthServiceV2 } from "@volkswagen-onehub/authservice";
import { NoReportAvailableError } from "../errors/NoReportAvailableError";

export interface IVcfMaintenanceStatusResponse {
    readonly data?: {
        readonly inspectionDue_days?: number | null;
        readonly inspectionDue_km?: number | null;
        readonly oilServiceDue_days?: number | null;
        readonly oilServiceDue_km?: number | null;
    };
}

export interface IVcfHealthReportsResponse {
    readonly data: IVcfHealthReportData[];
}

export interface IVcfHealthReportData {
    readonly id?: number;
    readonly carCapturedTimestamp?: string;
    readonly mileage_km?: number;
}

export interface IVcfHealthReportResponse {
    readonly data: {
        readonly carCapturedTimestamp?: string;
        readonly mileage_km?: number;
        readonly warningLights: IVcfWarningLight[];
    };
}

export interface IVcfWarningLight {
    readonly text?: string;
    readonly priority?: string;
    readonly icon?: string;
    readonly category?: string;
    readonly iconName?: string;
    readonly messageId?: string;
    readonly customerRelevance?: boolean;
}

export interface IVcfHealthReportSettingsResponse {
    readonly data: {
        readonly timeBasedSupported?: boolean;
        readonly timeBased?: IVcfTimeBasedSettings;
        readonly distanceBasedSupported?: boolean;
        readonly distanceBased?: IVcfDistanceBasedSettings;
        readonly maintenanceBasedSupported?: boolean;
        readonly maintenanceBased?: IVcfMaintenanceBasedSettings;
    };
}

export interface IVcfHealthReportSettingsUpdate {
    readonly timeBased: IVcfTimeBasedSettings;
    readonly distanceBased: IVcfDistanceBasedSettings;
    readonly maintenanceBased: IVcfMaintenanceBasedSettings;
}

export interface IVcfTimeBasedSettings {
    readonly collectionPeriod?: string;
    readonly dayOfMonth?: number;
    readonly monthsBetweenReports?: number;
    readonly hour?: number;
    readonly minute?: number;
}

export interface IVcfDistanceBasedSettings {
    readonly distance_km?: number;
    readonly start_km?: number;
}

export interface IVcfMaintenanceBasedSettings {
    readonly distance_km?: number;
}

// https://cardata.apps.emea.vwapps.io/documentation#tag/Vehicle-Health
export class VcfHealthClient {
    
    private readonly resourceHostId: string;

    private getGdcType = (isWcar: boolean) => isWcar ? `myvw-wcar-${this.stage}` : `myvw-mbb-${this.stage}`;

    constructor(private readonly vhrLanguage: string, private readonly authService: IAuthServiceV2, private readonly stage: string) {
        this.resourceHostId = `myvw-vcf-${stage}`;
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Vehicle-Health/operation/getMaintenanceStatus
    public async getMaintenanceStatus(vin: string, isWcar: boolean): Promise<IVcfMaintenanceStatusResponse> {
        const url = `vehicles/${vin}/maintenance/status?gdc=${this.getGdcType(isWcar)}`;
        const headers = {'User-Id': '__userId__'};
        const response = await this.authService.get(this.resourceHostId, url, headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return await response.json() as IVcfMaintenanceStatusResponse;
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Vehicle-Health/operation/getWarningLightReports
    public async getHealthReports(vin: string, isWcar: boolean): Promise<IVcfHealthReportsResponse | undefined> {
        const url = `vehicles/${vin}/warninglights?gdc=${this.getGdcType(isWcar)}`;
        const headers = {'User-Id': '__userId__', 'Accept-Language': this.vhrLanguage};
        const response = await this.authService.get(this.resourceHostId, url, headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return await response.json() as IVcfHealthReportsResponse;
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Vehicle-Health/operation/getWarningLightReportByID
    public async deleteHealthReport(vin: string, isWcar: boolean, id: number): Promise<void> {
        const url = `vehicles/${vin}/warninglights/${id}?gdc=${this.getGdcType(isWcar)}`;
        const headers = {'User-Id': '__userId__'};
        const response = await this.authService.delete(this.resourceHostId, url, undefined, headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Vehicle-Health/operation/getLastWarningLightsReport
    public async getLatestHealthReport(vin: string, isWcar: boolean): Promise<IVcfHealthReportResponse | undefined> {
        const url = `vehicles/${vin}/warninglights/last?gdc=${this.getGdcType(isWcar)}`;
        const headers = {'User-Id': '__userId__', 'Accept-Language': this.vhrLanguage};
        const response = await this.authService.get(this.resourceHostId, url, headers);

        if (response.status === 404) {
            throw new NoReportAvailableError();
        }

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return await response.json() as IVcfHealthReportResponse;
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Vehicle-Health/operation/getWarningLightsSettings
    public async getHealthReportSettings(vin: string, isWcar: boolean): Promise<IVcfHealthReportSettingsResponse | undefined> {
        const url = `vehicles/${vin}/warninglights/settings?gdc=${this.getGdcType(isWcar)}`;
        const headers = {'User-Id': '__userId__', 'Accept-Language': this.vhrLanguage};
        const response = await this.authService.get(this.resourceHostId, url, headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return await response.json() as IVcfHealthReportSettingsResponse;
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Vehicle-Health/operation/setWarningLightsSettings
    public async saveHealthReportSettings(vin: string, isWcar: boolean, healthReportSettings: IVcfHealthReportSettingsUpdate): Promise<void> {
        const url = `vehicles/${vin}/warninglights/settings?gdc=${this.getGdcType(isWcar)}`;
        const headers = {'User-Id': '__userId__', 'Content-Type': 'application/json'};
        const response = await this.authService.put(this.resourceHostId, url, JSON.stringify(healthReportSettings), headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Vehicle-Health/operation/triggerWarningLightsReport
    public async triggerWarningLightsUpdate(vin: string, isWcar: boolean): Promise<void> {
        const url = `vehicles/${vin}/warninglights/update?gdc=${this.getGdcType(isWcar)}`;
        const headers = {'User-Id': '__userId__', 'Content-Type': 'application/json'};
        const response = await this.authService.post(this.resourceHostId, url, JSON.stringify({}), headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }
    }

    // https://cardata.apps.emea.vwapps.io/documentation#tag/Vehicle-Health/operation/getWarningLightReportByID
    public async getHealthReportById(vin: string, isWcar: boolean, id: number): Promise<IVcfHealthReportResponse | undefined> {
        const url = `vehicles/${vin}/warninglights/${id}?gdc=${this.getGdcType(isWcar)}`;
        const headers = {'User-Id': '__userId__', 'Accept-Language': this.vhrLanguage};
        const response = await this.authService.get(this.resourceHostId, url, headers);

        if (!response.ok) {
            throw new Error(`Request failed with status code ${response.status}`);
        }

        return await response.json() as IVcfHealthReportResponse;
    }
}

